import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import type { AuthMethods, StartEmailCodeResult } from '../services/account/authApi'
import {
  type AuthUser,
  deleteAccount,
  fetchAuthMethods,
  isAuthAvailable,
  isPasskeySupported,
  requestEmailCode,
  signInWithEmailCode,
  signInWithGoogle,
  signInWithPasskey,
  signOutUser,
  subscribeToAuthChanges,
} from '../services/account/authService'

// The *player* account (optional sign-in shared with the mobile app). This is
// unrelated to AuthContext, which is the legacy password login for setters.

interface PlayerAuthContextValue {
  user: AuthUser | null
  initializing: boolean
  /** False when this build has no Firebase configuration: nothing can be signed in to. */
  available: boolean
  /**
   * Which server-verified methods to offer. Everything is `false` until the
   * backend answers (and stays `false` if it can't be reached), so a method
   * only appears once we know it works.
   */
  methods: AuthMethods
  /**
   * Whether to offer passkeys: the backend has them on AND this browser can use
   * them AND sign-in works in this build. Like `methods`, it fails closed.
   */
  passkeyAvailable: boolean
  requestEmailCode: (email: string) => Promise<StartEmailCodeResult>
  signInWithEmailCode: (email: string, code: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithPasskey: () => Promise<void>
  signOut: () => Promise<void>
  /** Permanently deletes the account. Rejects with AUTH_REAUTH_REQUIRED if the last sign-in was not recent. */
  deleteAccount: () => Promise<void>
}

const NO_METHODS: AuthMethods = { emailOtp: false, passkey: false }

const PlayerAuthContext = createContext<PlayerAuthContextValue | undefined>(undefined)

export const PlayerAuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [initializing, setInitializing] = useState(true)
  const [methods, setMethods] = useState<AuthMethods>(NO_METHODS)
  const [deviceSupportsPasskeys, setDeviceSupportsPasskeys] = useState(false)
  const available = isAuthAvailable()

  useEffect(() => {
    return subscribeToAuthChanges((nextUser) => {
      setUser(nextUser)
      setInitializing(false)
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    void fetchAuthMethods().then((offered) => {
      if (!cancelled) setMethods(offered)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void isPasskeySupported().then((supported) => {
      if (!cancelled) setDeviceSupportsPasskeys(supported)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const passkeyAvailable = available && methods.passkey && deviceSupportsPasskeys

  const value = useMemo<PlayerAuthContextValue>(
    () => ({
      user,
      initializing,
      available,
      methods,
      passkeyAvailable,
      requestEmailCode,
      signInWithEmailCode: async (email, code) => {
        await signInWithEmailCode(email, code)
      },
      signInWithGoogle: async () => {
        await signInWithGoogle()
      },
      signInWithPasskey: async () => {
        await signInWithPasskey()
      },
      signOut: signOutUser,
      deleteAccount,
    }),
    [user, initializing, available, methods, passkeyAvailable]
  )

  return <PlayerAuthContext.Provider value={value}>{children}</PlayerAuthContext.Provider>
}

export const usePlayerAuth = (): PlayerAuthContextValue => {
  const context = useContext(PlayerAuthContext)
  if (!context) {
    throw new Error('usePlayerAuth must be used within a PlayerAuthProvider')
  }
  return context
}
